import { registerLocaleData } from '@angular/common';
import localeEn from '@angular/common/locales/en';
import localeEs from '@angular/common/locales/es-US';
import { EnvironmentProviders, LOCALE_ID, makeEnvironmentProviders } from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { APP_LANGUAGES, localeForLanguage } from '@core/constants/language.constants';
import { DRDP_DATE_FORMATS } from './date-formats';
import { DrdpDateAdapter } from './drdp-date-adapter';
import { resolveStartupLanguage } from './startup-language';

/**
 * Angular's `DatePipe`, `DecimalPipe` and friends read locale data that has to
 * be registered up front. Both locales are registered regardless of the active
 * language, because the user can switch at runtime.
 */
registerLocaleData(localeEn, 'en-US');
registerLocaleData(localeEs, 'es-US');

/**
 * Locale wiring for dates, times and numbers.
 *
 * `LOCALE_ID` is resolved once at startup — Angular injects it into pipes at
 * construction and it cannot meaningfully change afterwards. Runtime switching
 * is therefore handled by two things that CAN change:
 *
 *   - `LocalizedDatePipe`, which reads the active language on each evaluation
 *   - `DateAdapter.setLocale()`, called by `LanguageService` on every switch
 *
 * `LOCALE_ID` still matters for anything using the stock pipes, so it is set to
 * the user's saved preference rather than left at the `en-US` default.
 */
export function provideAppLocale(): EnvironmentProviders {
  const startupLocale = localeForLanguage(resolveStartupLanguage());

  return makeEnvironmentProviders([
    { provide: LOCALE_ID, useValue: startupLocale },

    // Seeds the datepicker's locale. LanguageService keeps it in sync afterwards.
    { provide: MAT_DATE_LOCALE, useValue: startupLocale },

    // Parses typed input in the same field order the locale displays.
    // Without this, es-US shows day-first but reads back month-first.
    { provide: DateAdapter, useClass: DrdpDateAdapter, deps: [MAT_DATE_LOCALE] },

    { provide: MAT_DATE_FORMATS, useValue: DRDP_DATE_FORMATS },
  ]);
}

/** Every locale the app can render, for tooling and tests. */
export const APP_LOCALES = APP_LANGUAGES.map((lang) => lang.locale);
